import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { api, ApiError } from '../lib/api'
import { recordServerTime, serverNow, useCountdown } from '../lib/clock'
import { formatLeadTime, formatTimestamp, localInputToMs, msToLocalInput } from '../lib/datetime'
import CopyLink from './CopyLink'
import type { DraftResponse, DraftState } from '../types/protocol'

interface Props {
  draft: DraftState
  commissionerToken: string
  onDraft: (draft: DraftState) => void
}

// DraftEngine::LOTTERY_MIN_LEAD, mirrored. The server refuses anything inside
// this window with a 422, and refusing it here first is what makes the message
// specific: past the draw the order is final, so "too soon" has to be caught
// while it is still a typo rather than after it is unfixable.
const MIN_LEAD_MS = 30_000

// Setup.tsx has no cable and refetches only on window focus, so nothing would
// tell this page the sweeper had fired. Two rates rather than one: a lottery
// set for next Tuesday does not need a 2s poll for six days, and one set for
// ten seconds from now needs the reveal on screen without the commissioner
// touching anything.
const NEAR_WINDOW_MS = 60_000
const POLL_NEAR_MS = 2_000
const POLL_FAR_MS = 30_000

const PRESET_MINUTES = [15, 30, 60]

const TOO_SOON =
  'Pick a time at least 30 seconds out — the draw fires the moment the clock reaches it, and the order is final afterwards.'

const BUTTON =
  'rounded bg-signal px-4 py-2 text-sm font-semibold text-ink hover:bg-signal-dim disabled:opacity-50'
const SECONDARY =
  'rounded border border-line-strong px-3 py-1.5 text-sm font-medium text-ink-hi hover:bg-surface-3 disabled:opacity-40'

export default function LotteryPanel({ draft, commissionerToken, onDraft }: Props) {
  const [value, setValue] = useState(() =>
    msToLocalInput(draft.lottery_at ? Date.parse(draft.lottery_at) : null),
  )
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Unreachable on this page — Setup redirects to /control the instant status
  // changes — but SettingsForm computes exactly the same unreachable `locked`,
  // and matching that is cheaper than explaining why this one panel is the
  // exception.
  const locked = draft.status !== 'setup'
  const revealed = draft.lottery_revealed_at != null
  const lotteryMs = draft.lottery_at ? Date.parse(draft.lottery_at) : null
  const lotteryUrl = `${window.location.origin}/lottery/${draft.join_code}`

  // Once the latch is set the countdown has nothing left to count.
  const { remainingMs, expired } = useCountdown(revealed ? null : lotteryMs)

  const chosenMs = localInputToMs(value)
  const tooSoon = chosenMs != null && chosenMs - serverNow() <= MIN_LEAD_MS
  const canSubmit = !locked && !revealed && !saving && chosenMs != null && !tooSoon
  // Gated on the countdown having expired, because run_lottery! requires
  // lottery_at <= Time.current and no-ops silently otherwise. A button that
  // does nothing when pressed is worse than one that is disabled with a reason.
  const canDraw = !locked && !revealed && lotteryMs != null && expired && !saving

  // `near` and not `remainingMs`: remainingMs changes every 250ms, so putting
  // it in the dependency array would tear the interval down and rebuild it
  // eight times before a 2s tick could ever fire. `near` flips exactly once, so
  // the interval is rebuilt exactly once.
  const near = remainingMs == null || remainingMs <= NEAR_WINDOW_MS
  const pollMs = near ? POLL_NEAR_MS : POLL_FAR_MS

  useEffect(() => {
    if (locked || revealed || lotteryMs == null) return

    // Deliberately not Setup's own refetch: that one drops the whole console
    // behind an error screen on a single failed request, and a background tick
    // must never be able to do that. A dropped poll is silent — the next one
    // retries.
    const id = setInterval(() => {
      void api<DraftResponse>('/drafts/resolve', { commissionerToken })
        .then((response) => {
          recordServerTime(response.server_now_ms)
          onDraft(response.draft)
        })
        .catch(() => {})
    }, pollMs)

    return () => clearInterval(id)
  }, [locked, revealed, lotteryMs, pollMs, commissionerToken, onDraft])

  async function save(event: FormEvent) {
    event.preventDefault()
    const ms = localInputToMs(value)
    if (ms == null) {
      setError('Pick a date and time first.')
      return
    }
    // Re-checked at submit and not only at render: the panel may have been
    // sitting open since before the chosen time went past.
    if (ms - serverNow() <= MIN_LEAD_MS) {
      setError(TOO_SOON)
      return
    }
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const response = await api<DraftResponse>(`/drafts/${draft.join_code}/lottery`, {
        method: 'POST',
        commissionerToken,
        // `lottery_at`, matching what the controller permits. Flat body —
        // wrap_parameters is off.
        body: { lottery_at: new Date(ms).toISOString() },
      })
      onDraft(response.draft)
      setSaved(true)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not schedule the lottery.')
    } finally {
      setSaving(false)
    }
  }

  async function cancel() {
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const response = await api<DraftResponse>(`/drafts/${draft.join_code}/lottery`, {
        method: 'DELETE',
        commissionerToken,
      })
      onDraft(response.draft)
      setValue('')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not cancel the lottery.')
    } finally {
      setSaving(false)
    }
  }

  async function drawNow() {
    setSaving(true)
    setSaved(false)
    setError(null)
    try {
      const response = await api<DraftResponse>(`/drafts/${draft.join_code}/lottery/run`, {
        method: 'POST',
        commissionerToken,
      })
      onDraft(response.draft)
      // run_lottery! no-ops silently when its guards fail: the API deliberately
      // adds no error class for it, because the base controller rescues only
      // four DraftEngine errors and an unrescued one is a 500. An unmoved latch
      // is therefore the only signal that nothing happened, so read it rather
      // than claiming success.
      if (response.draft.lottery_revealed_at == null) {
        setError('The server did not run the draw. Check the app is still up, then try again.')
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not run the draw.')
    } finally {
      setSaving(false)
    }
  }

  function usePreset(minutes: number) {
    // serverNow() and not Date.now(): a laptop running two minutes slow would
    // otherwise write a "+15 minutes" the server reads as thirteen, and one
    // running two minutes fast would write one the server refuses outright.
    setValue(msToLocalInput(serverNow() + minutes * 60_000))
    setSaved(false)
    setError(null)
  }

  return (
    <section className="panel p-6">
      <h2 className="eyebrow">Draft lottery</h2>

      {revealed ? (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-ink-mid">
            The order was drawn {formatTimestamp(draft.lottery_revealed_at)} and is final.
          </p>
          <CopyLink label="Send the league the lottery:" url={lotteryUrl} />
        </div>
      ) : locked ? (
        <p className="mt-4 text-sm text-ink-mid">
          The draft has started — a lottery can only be scheduled during setup.
        </p>
      ) : (
        <>
          <p className="mt-2 text-sm text-ink-lo">
            Everyone with the link watches the same countdown, then the same draw. The order is
            final afterwards.
          </p>

          {lotteryMs != null && (
            <p className="mt-4 flex flex-wrap items-baseline gap-x-3 text-sm text-ink-mid">
              <span className="font-mono tabular-nums text-ink-hi">
                Drawing in {formatLeadTime(remainingMs)}
              </span>
              <span className="text-ink-lo">{formatTimestamp(draft.lottery_at)}</span>
            </p>
          )}

          {lotteryMs != null && expired && (
            <p className="mt-2 text-sm text-ink-mid">
              The draw is due. Give it a few seconds — if nothing happens, use Draw now.
            </p>
          )}

          <form onSubmit={(event) => void save(event)} className="mt-4 space-y-4">
            <label className="flex flex-col gap-1 text-sm text-ink-mid">
              When the draw happens
              <input
                type="datetime-local"
                value={value}
                onChange={(event) => {
                  setValue(event.target.value)
                  setSaved(false)
                  setError(null)
                }}
                className="rounded border border-line-strong bg-ink px-3 py-2 text-ink-hi"
              />
            </label>

            {tooSoon && <p className="text-sm text-crit">{TOO_SOON}</p>}

            <div className="flex flex-wrap gap-2">
              {PRESET_MINUTES.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => usePreset(minutes)}
                  className={SECONDARY}
                >
                  {minutes === 60 ? '+1 hour' : `+${minutes} min`}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" disabled={!canSubmit} className={BUTTON}>
                {saving ? 'Saving…' : lotteryMs != null ? 'Reschedule' : 'Schedule the lottery'}
              </button>
              {lotteryMs != null && (
                <button
                  type="button"
                  onClick={() => void cancel()}
                  disabled={saving}
                  className={SECONDARY}
                >
                  Cancel
                </button>
              )}
              {lotteryMs != null && (
                <button
                  type="button"
                  onClick={() => void drawNow()}
                  disabled={!canDraw}
                  className={SECONDARY}
                >
                  Draw now
                </button>
              )}
              {saved && <span className="text-sm text-signal">Saved</span>}
              {error && <span className="text-sm text-crit">{error}</span>}
            </div>
          </form>

          {/* Nothing to watch before one is scheduled, and pretending otherwise
              sends twelve people to a page that says "no lottery scheduled". */}
          {lotteryMs != null && (
            <div className="mt-4">
              <CopyLink label="Send the league the lottery:" url={lotteryUrl} />
            </div>
          )}
        </>
      )}
    </section>
  )
}
